import React, {useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Form, Field } from "formik";
import { Box, Flex } from "rebass";
import { toastr } from "react-redux-toastr";

import { getIsPlaying, getRate } from "../../../store/modules/audio/selectors";
import { getIsOrigin } from "../../../store/modules/display/selectors";
import { Mode } from "../../../store/modules/library/selectors";

import { DEFAULT_TEXT, FormTypes } from "./formSettings";

import { FormControl } from "./FormControl";
import { PDFViewer } from "../PDFViewer";
import { Player } from "../Player";
import { PromptLeaveRecord } from "./PromptLeaveRecord";
import { RecordInput } from "./RecordInput";
import { RecordMetaData } from "../RecordMetaData";
import { Scrollbar } from "../Scrollbar";
import { TitleInput } from "./TitleInput";

import { rateToValue } from "../../../utils/converSpeed";
import { speech } from "../../../index";

interface IProps extends FormTypes {
  mode: Mode;
  recordMetaData: {
    isLoading: boolean;
    bookType: string;
    coverImagePath?: string;
    error?: boolean;
    pdfDocumentPath?: string;
  };
}

const showControl = [Mode.edit];

export const RecordForm: React.FC<IProps> = ({ dirty, values, mode, recordMetaData}) => {
  const { recordId, title, text } = values;
  const { bookType, coverImagePath, error, pdfDocumentPath } = recordMetaData;
  const rate = useSelector(getRate);
  const isOrigin = useSelector(getIsOrigin);
  const isPlaying = useSelector(getIsPlaying);
  const wordCount = text.trim().split(/\s+/).length;
  /* convert text length to time */
  const time = useMemo(() => (
    (wordCount / rateToValue(rate)).toFixed(0)
  ), [wordCount, rate]);
  /* show toastr error */
  useEffect(() => {
    if (error) {
      toastr.error("Record", "Record is not exist.");
    }
  }, [error]);
  /* force cancel reading with unsubscribe */
  useEffect(() => {
    speech.cancel(true).then(() => {
      speech.setText(text || DEFAULT_TEXT);
    });

    return () => {
      speech.cancel().then(() => {
        speech.setText(DEFAULT_TEXT);
      });
    };
  }, [text]);

  return (
    <Form style={{ height: "100%", overflow: "hidden" }}>
      <PromptLeaveRecord
        recordId={recordId}
        title={title}
        dirty={dirty}
      />

      <Flex height="100%" flexDirection="column">
        <Box flex="1" css="overflow: hidden;">
          <Scrollbar>
            <Flex
              height="auto"
              width={2/3}
              mx="auto"
              flexDirection="column"
              css="min-height: 100%;"
            >
              <Box mt="30px">
                <Field
                  name="title"
                  component={TitleInput}
                  mode={mode}
                />
              </Box>

              <RecordMetaData
                title={title}
                bookType={bookType}
                time={time}
                coverImagePath={coverImagePath}
              />

              <Box
                flex="1"
                mb="10px"
              >
                {
                  isOrigin
                    ? <PDFViewer record={{ title, pdfDocumentPath }} />
                    : (
                      <Field
                        name="text"
                        component={RecordInput}
                        mode={mode}
                      />
                    )
                }
              </Box>
            </Flex>
          </Scrollbar>
        </Box>

        <Box css="position: relative;">
          <Player />

          {(dirty && showControl.includes(mode) && !isPlaying) && (
            <Box css="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 1;">
              <FormControl />
            </Box>
          )}
        </Box>
      </Flex>
    </Form>
  );
};
